import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Board from '../../components/Board';
import List from '../../components/List';
import TabContent from '../../components/TabContent';
import BlockTitle from '../../components/BlockTitle';
import memberCenterService from '../../../../service/memberCenterService';
import TableRenderComponent from './components/TableRenderComponent';
import { TAB_TYPE } from '../../constants';
import { getTabUrl } from '../../utils/tab';
import { hasFeature } from '../../../../utils/featureService';

const PAGE_LIMIT = 24;

const UserCreadits = () => {
  const [creadits, setCreadits] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pages: 0,
  });
  const hideCreditPointRecordBalance = hasFeature(
    'hide_credit_point_record_balance',
  );
  const { t } = useTranslation();

  const coloums = useMemo(() => {
    const coloum_map = {
      created_at: {
        id: 'created_at',
        key: 'created_at',
        title: t('store_credits.fields.date'),
        width: '16.667%',
      },
      remarks: {
        id: 'remarks',
        key: 'remarks',
        title: t('store_credits.fields.remarks'),
        width: '33.333%',
        mobileShow: false,
      },
      value: {
        id: 'value',
        key: 'value',
        title: t('store_credits.fields.value'),
        width: '16.6667%',
        mobileShow: false,
      },
      expire_at: {
        id: 'expire_at',
        key: 'expire_at',
        title: t('store_credits.fields.expiry_date'),
        width: '16.6667%',
      },
      credit_balance: {
        id: 'credit_balance',
        key: 'credit_balance',
        title: t('store_credits.fields.balance'),
      },
    };

    const baseColumns = [
      [coloum_map.remarks, coloum_map.value],
      coloum_map.created_at,
      coloum_map.remarks,
      coloum_map.value,
      coloum_map.expire_at,
    ];

    return hideCreditPointRecordBalance
      ? baseColumns
      : [...baseColumns, coloum_map.credit_balance];
  }, [t, hideCreditPointRecordBalance]);

  const fetchCreadits = useCallback(
    (page = 1) => {
      if (loading) {
        return;
      }
      setLoading(true);
      memberCenterService
        .fetchUserCredits({
          limit: PAGE_LIMIT,
          page,
        })
        .then((res) => {
          if (!res?.data?.items) {
            throw new Error('Error fetchUserCredits, No items!');
          }
          const { items = [], total } = res?.data || {};
          const newCreadits = (creadits || []).concat(items);
          setCreadits(newCreadits);
          setPagination({
            page,
            pages: window.Math.ceil(total / PAGE_LIMIT),
          });
        }, [])
        .catch((err) => {
          console.warn('fetchUserCredits catch', err);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [creadits, loading],
  );

  const onLoadMore = useCallback(() => {
    if (loading) {
      return;
    }
    fetchCreadits(pagination.page + 1);
  }, [fetchCreadits, loading, pagination.page]);

  useEffect(() => {
    fetchCreadits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TabContent url={getTabUrl(TAB_TYPE.USER_CREADITS)}>
      <BlockTitle
        showSkeleton={!creadits}
        title={t('store_credits.credit_history')}
      />
      <Board showSkeleton={!creadits}>
        <div className="member-center__board-block" key="l">
          <div className="member-center__board-left">
            <span className="member-center__board-left-title">
              {t('store_credits.credit_balance')}
            </span>
            <span className="member-center__board-left-value-container">
              <span className="num">{creadits?.[0]?.credit_balance || 0}</span>
              <span className="visible-xs-inline">
                {t('store_credits.title')}
              </span>
            </span>
          </div>
        </div>
      </Board>
      <List
        listKey="_id"
        list={creadits}
        coloums={coloums}
        emptyProps={{
          description: t('store_credits.list.empty.description'),
          buttonProps: {
            text: t('member_center.list.empty.buttonText'),
            link: '/products',
          },
        }}
        showSkeleton={!creadits}
        skeleton={{
          rows: 6,
          mobileRows: 3,
          mobileSizes: [
            [43.5, 9.2],
            [15.24, 33.65],
            [22.22, 26.35],
            [29.21, 13.02],
          ],
        }}
        paginationProps={{
          pagination: pagination,
          loading: creadits?.length > 0 && loading,
          onLoadMore,
        }}
        RenderComponent={TableRenderComponent}
      />
    </TabContent>
  );
};

UserCreadits.propTypes = {};

export default UserCreadits;
